/**
 * News page — the strings of the PAGE CHROME (headings, labels, dates, the
 * unread indicator). Not the release posts: those are authored English text
 * stored in the database and are deliberately not translated.
 *
 * TWO TABLES, ONE SHAPE
 *   NEWS_STRINGS     English. Used as the inline `defaultValue` for every
 *                    lookup, so the page renders correctly even if no
 *                    translation file knows about it. Same pattern as
 *                    EVIDENCE_STRINGS in components/EvidencePanel.tsx.
 *   NEWS_STRINGS_EL  Greek.
 *
 * PLURALS use i18next's `_one` / `_other` suffixes and are selected by the
 * `count` variable. A flat key where the code passes `count` renders raw, which
 * is why `tn()` below exists: it picks the right English form for the fallback.
 *
 * REGISTRATION
 * `registerNewsTranslations()` adds both tables to the running i18n instance, so
 * the page and the nav entry are fully bilingual without touching
 * i18n/index.ts. Once the same keys are merged into that file (see the hand-over
 * note) the call is redundant but harmless: it deep-merges WITHOUT overwriting.
 */

import i18n from 'i18next';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export const NEWS_STRINGS = {
  // Nav entry (the Shell reads these through t(item.labelKey))
  'nav.news': 'News',
  'nav.shortNews': 'News',

  'news.title': 'News',
  'news.subtitle': 'What is new in OpenComply, release by release.',
  // First line of every post. {{version}} is the release number.
  'news.announce': 'Version {{version}} of OpenComply has been released.',
  'news.released': 'Released {{date}}',
  'news.new': 'New',
  'news.latest': 'Latest',
  'news.kind.MAJOR': 'Major release',
  'news.kind.MINOR': 'Intermediary release',
  'news.kind.PATCH': 'Patch release',
  'news.category.FEATURE': 'New features',
  'news.category.IMPROVEMENT': 'Quality of life',
  'news.category.FIX': 'Bug fixes',
  'news.empty.title': 'No news yet',
  'news.empty.body': 'Release announcements will appear here.',
  'news.loadFailed': 'Could not load the news.',
  'news.retry': 'Try again',
  'news.loadMore': 'Show older releases',
  'news.loadingMore': 'Loading…',
  'news.showing_one': 'Showing {{count}} of {{total}} release',
  'news.showing_other': 'Showing {{count}} of {{total}} releases',
  'news.endOfHistory': 'That is where the story begins.',
  'news.englishNote': 'Release posts are written in English.',
  // Nav indicator. {{count}} drives the plural.
  'news.unread_one': '{{count}} new release',
  'news.unread_other': '{{count}} new releases',
  'news.navUnread_one': 'News, {{count}} new release',
  'news.navUnread_other': 'News, {{count}} new releases',
} as const;

export const NEWS_STRINGS_EL: Record<keyof typeof NEWS_STRINGS, string> = {
  'nav.news': 'Νέα',
  'nav.shortNews': 'Νέα',

  'news.title': 'Νέα',
  'news.subtitle': 'Τι νέο υπάρχει στο OpenComply, έκδοση προς έκδοση.',
  'news.announce': 'Κυκλοφόρησε η έκδοση {{version}} του OpenComply.',
  'news.released': 'Κυκλοφόρησε στις {{date}}',
  'news.new': 'Νέο',
  'news.latest': 'Τελευταία',
  'news.kind.MAJOR': 'Μεγάλη έκδοση',
  'news.kind.MINOR': 'Ενδιάμεση έκδοση',
  'news.kind.PATCH': 'Έκδοση διορθώσεων',
  'news.category.FEATURE': 'Νέες δυνατότητες',
  'news.category.IMPROVEMENT': 'Βελτιώσεις στη χρήση',
  'news.category.FIX': 'Διορθώσεις σφαλμάτων',
  'news.empty.title': 'Δεν υπάρχουν νέα ακόμη',
  'news.empty.body': 'Οι ανακοινώσεις των εκδόσεων θα εμφανίζονται εδώ.',
  'news.loadFailed': 'Δεν ήταν δυνατή η φόρτωση των νέων.',
  'news.retry': 'Δοκιμάστε ξανά',
  'news.loadMore': 'Εμφάνιση παλαιότερων εκδόσεων',
  'news.loadingMore': 'Φόρτωση…',
  'news.showing_one': 'Εμφανίζεται {{count}} από {{total}} έκδοση',
  'news.showing_other': 'Εμφανίζονται {{count}} από {{total}} εκδόσεις',
  'news.endOfHistory': 'Εδώ ξεκινά η ιστορία.',
  'news.englishNote': 'Οι αναρτήσεις των εκδόσεων είναι γραμμένες στα αγγλικά.',
  'news.unread_one': '{{count}} νέα έκδοση',
  'news.unread_other': '{{count}} νέες εκδόσεις',
  'news.navUnread_one': 'Νέα, {{count}} νέα έκδοση',
  'news.navUnread_other': 'Νέα, {{count}} νέες εκδόσεις',
};

export type NewsKey = keyof typeof NEWS_STRINGS;
/** Plural keys by their BASE name, which is what the code passes (with `count`). */
export type NewsPluralKey = 'news.showing' | 'news.unread' | 'news.navUnread';

/**
 * Add both tables to the running i18n instance. Deep-merged, never
 * overwriting: a translation already present in i18n/index.ts wins.
 */
let registered = false;
export function registerNewsTranslations(): void {
  if (registered) return;
  registered = true;
  for (const [lng, table] of [['en', NEWS_STRINGS], ['el', NEWS_STRINGS_EL]] as const) {
    // Flat dotted keys are resolved by i18next's ignoreJSONStructure (default
    // on), so the tables need no nesting.
    i18n.addResources(lng, 'translation', table as Record<string, string>);
  }
}
registerNewsTranslations();

/**
 * Translate with the built-in English as the fallback. Pass `count` for a
 * plural: the fallback is then the `_one` or `_other` English form.
 */
export function useNewsT() {
  const { t, i18n: instance } = useTranslation();

  const tx = useCallback(
    (key: NewsKey | NewsPluralKey, options?: Record<string, unknown>): string => {
      const table = NEWS_STRINGS as Record<string, string>;
      const count = options?.count;
      const fallback =
        table[key] ??
        (typeof count === 'number' ? table[`${key}${count === 1 ? '_one' : '_other'}`] : undefined) ??
        key;
      return t(key, { defaultValue: fallback, ...options }) as string;
    },
    [t],
  );

  /** A release date ('YYYY-MM-DD') in the viewer's language, with no timezone drift. */
  const formatDate = useCallback(
    (isoDate: string): string =>
      new Intl.DateTimeFormat(instance.language, { dateStyle: 'long', timeZone: 'UTC' })
        .format(new Date(`${isoDate}T00:00:00.000Z`)),
    [instance.language],
  );

  return { tx, formatDate, language: instance.language };
}
